import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import StatusChip from "../../../../../../../components/StatusChip";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import { ChartActionStrip } from "../../../../../../../components/app/provider/patient/chart";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import { useGetAllPatientForms } from "../../../../../../../queries/patient-chart/forms/use-get-all-patient-forms";
import { formatDateTime } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../../utils/utils";
import {
  FORM_TYPE_MAPPER,
  INTAKE_FORM_SECTIONS_MAPPER,
} from "../../../../scheduling/appointment/appointment-constant";
import IntakePreviewModal from "../../../../scheduling/appointment/encounter/content-section/intake-preview/IntakePreviewModal";
import SendPatientFormsModal from "./SendPatientFormsModal";

const PatientChartForms = () => {
  const [sendFormsDisclosure, previewIntakeDisc] = useReduceDisclosure(2);
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [_gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams();

  const { data: patientForms } = useGetAllPatientForms({
    patientId: patientId ?? "",
    pagination,
    sortDirection: "desc",
    xTENANTID: getXtenantId(),
  });

  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  const ALL_COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "name",
      headerName: "Form Name",
      flex: 0.6,
      width: 200,
      renderCell: ({ value }) =>
        INTAKE_FORM_SECTIONS_MAPPER[value] ||
        replaceUnderScoreAddSpace(value) ||
        "-",
    },
    {
      field: "formType",
      headerName: "Form Type",
      flex: 0.4,
      minWidth: 100,
      renderCell: ({ row, value }) =>
        FORM_TYPE_MAPPER[row?.name] || value || "-",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.35,
      minWidth: 90,
      renderCell: ({ value }) =>
        value ? (
          <StatusChip
            label={
              value === "COMPLETED"
                ? "Submitted"
                : value === "PENDING"
                  ? "Pending"
                  : replaceUnderScoreAddSpace(value)
            }
            variant={value === "COMPLETED" ? "success" : "pending"}
          />
        ) : (
          "-"
        ),
    },
    {
      field: "created",
      headerName: "Sent On",
      flex: 0.5,
      minWidth: 130,
      renderCell: ({ value }) => formatDateTime(value),
    },
    {
      field: "sendBy",
      headerName: "Sent By",
      flex: 0.5,
      minWidth: 100,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "submittedOn",
      headerName: "Form Type- Submitted on",
      flex: 0.6,
      minWidth: 150,
      renderCell: ({ value }) => formatDateTime(value) || "-",
    },
    {
      field: "submittedBy",
      headerName: "Submitted By",
      flex: 0.5,
      minWidth: 100,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <ActionMenu
          options={[
            {
              value: "view",
              label: "View",
              isDisabled: row?.status !== "COMPLETED",
              hoverMsg: "View option is Available for Completed Forms only",
            },
          ]}
          onActionClick={() => {
            // console.log("val here check ", section.name);
            const sectionData = {
              section: row?.name,
              content: row?.content,
            };
            previewIntakeDisc.open(sectionData);
          }}
        />
      ),
    },
  ]);

  const MINIMIZED_FIELDS = new Set([
    "name",
    "sendBy",
    "submittedOn",
    "status",
    "created",
  ]);
  const COLUMNS = isVisitNotePage
    ? ALL_COLUMNS.filter((col) => MINIMIZED_FIELDS.has(col.field))
    : ALL_COLUMNS;

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SendPatientFormsModal disclosureHost={sendFormsDisclosure} />

      <IntakePreviewModal
        disclosureHost={previewIntakeDisc}
        selectedSection={previewIntakeDisc?.data?.section}
      />

      <ChartActionStrip
        endContent={
          <ChartActionStrip.Button
            startIcon={<AddIcon />}
            onClick={() => sendFormsDisclosure.open()}
          >
            Send Patient Forms
          </ChartActionStrip.Button>
        }
      />

      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={patientForms?.data?.content ?? []}
        rowCount={patientForms?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, isVisitNotePage)}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
      />
    </Box>
  );
};

export default PatientChartForms;
